import { useQueryClient } from '@tanstack/react-query'
import { Loader2Icon } from 'lucide-react'
import { useState } from 'react'

import { Banner, Button } from 'slab'
import { usePostEmployersAcaReportingConfirmAleStatus } from 'utils/web_api'

import AleQuestionSection from './AleQuestionSection'

interface AleConfirmationFormProps {
  planYear: number
  onConfirmed: (aleStatus: boolean) => void
  initialAleStatus?: boolean | null
  buttonText?: string
  showFormTypeHint?: boolean
}

export default function AleConfirmationForm ({
  planYear,
  onConfirmed,
  initialAleStatus = null,
  buttonText = 'Confirm ALE status',
  showFormTypeHint = true
}: AleConfirmationFormProps) {
  const [aleStatus, setAleStatus] = useState<boolean | null>(initialAleStatus)
  const queryClient = useQueryClient()

  const confirmMutation = usePostEmployersAcaReportingConfirmAleStatus({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as string[]
            return queryKey[0] === '/employers/aca_reporting'
          }
        })
        onConfirmed(variables.data.ale_status)
      }
    }
  })

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault()

    if (aleStatus === null) return

    confirmMutation.mutate({
      data: {
        plan_year: planYear,
        ale_status: aleStatus
      }
    })
  }

  const isSubmitting = confirmMutation.isPending

  return (
    <div className='space-y-4'>
      <AleQuestionSection
        aleStatus={aleStatus}
        onAleStatusChange={setAleStatus}
        showFormTypeHint={showFormTypeHint}
      />

      {confirmMutation.isError && (
        <Banner type='critical' title='Unable to confirm ALE status'>
          {(confirmMutation.error as Error)?.message ?? 'An error occurred. Please try again.'}
        </Banner>
      )}

      <div className='flex justify-start'>
        <Button
          onClick={handleSubmit}
          disabled={aleStatus === null || isSubmitting}
          variant='primary'
        >
          {isSubmitting
            ? (
              <span className='flex items-center gap-2'>
                <Loader2Icon className='w-4 h-4 animate-spin' />
                Confirming...
              </span>
              )
            : buttonText}
        </Button>
      </div>
    </div>
  )
}
